
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/utils/PageTransition';
import { Cpu, ArrowLeft, ArrowUpRight, ChevronRight, Camera, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';

const IoTSolutionsPage = () => {
  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-32 pb-20">
          <div className="container mx-auto px-6">
            <div className="mb-6">
              <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to all services
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-12 mb-16">
              <div className="md:col-span-2">
                <div className="animate-fade-in">
                  <div className="bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400 p-3 rounded-xl inline-block mb-4">
                    <Cpu className="h-10 w-10" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                    IoT <span className="text-primary">Solutions</span>
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Connect devices and systems to create intelligent networks that deliver real-time data.
                  </p>
                </div>

                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    The Internet of Things (IoT) enables physical devices to collect and exchange data, creating intelligent networks that drive efficiency and innovation. Our IoT solutions help businesses connect devices, monitor systems, and analyze real-time data to optimize operations.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We design and implement end-to-end IoT solutions that integrate seamlessly with your existing infrastructure. From sensor selection and deployment to data collection and analysis, we ensure your IoT ecosystem delivers actionable insights and measurable value.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Gain real-time visibility into operations and assets</li>
                    <li>Improve efficiency through automated monitoring and control</li>
                    <li>Enhance decision-making with data-driven insights</li>
                    <li>Reduce downtime through predictive maintenance</li>
                    <li>Optimize resource utilization and reduce waste</li>
                    <li>Create new revenue streams through connected products and services</li>
                  </ul>
                  
                  <h2>Advanced IoT Technologies</h2>
                  <p>
                    Our IoT solutions leverage cutting-edge technologies including:
                  </p>
                  
                  <h3>Computer Vision</h3>
                  <p>
                    Integrate visual capabilities into your IoT ecosystem with advanced computer vision technologies. From quality control in manufacturing to security systems and autonomous navigation, our computer vision solutions enable machines to "see" and interpret the visual world.
                  </p>
                  
                  <h3>Robotics Automation</h3>
                  <p>
                    Combine IoT connectivity with advanced robotics to create intelligent automated systems. Our robotics automation solutions range from collaborative robots (cobots) for manufacturing to autonomous mobile robots for logistics and fulfillment operations.
                  </p>
                </div>
              </div>

              <div className="md:col-span-1">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32">
                  <h3 className="text-xl font-semibold mb-4">Our IoT Services</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>IoT Architecture Design</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Sensor Networks</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Edge Computing</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Real-time Monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <Camera className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Computer Vision</span>
                    </li>
                    <li className="flex items-start">
                      <ScanLine className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Robotics Automation</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Industrial IoT (IIoT)</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Smart Device Integration</span>
                    </li>
                  </ul>

                  <div className="mt-8">
                    <Link to="/contact">
                      <Button className="w-full">
                        Contact Us
                        <ArrowUpRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-cyan-50 dark:from-green-950/30 dark:to-cyan-950/30 p-8 rounded-2xl mb-16">
              <h2 className="text-2xl font-semibold mb-4">Ready to build connected systems?</h2>
              <p className="text-muted-foreground mb-6">
                Let's discuss how our IoT solutions can help you create intelligent, connected systems that drive innovation.
              </p>
              <Link to="/contact">
                <Button>
                  Get Started
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default IoTSolutionsPage;
